import * as React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  FORMAT_LABEL,
  OUTCOME_LABEL,
  RunReport,
  SKIP_LABEL,
  MERGE_SKIP_LABEL,
  REFRESH_SKIP_LABEL,
  SNIPPET_SKIP_LABEL,
  ThreadEntry,
} from '../types';
import AskBox from './AskBox';
import { Section, VerdictChip, fmt, formatDay, formatTime } from './common';

interface Props {
  run: RunReport;
  onOpenItem: (id: string) => void;
  onAsk: (question: string) => Promise<ThreadEntry[]>;
  onAddRule: (text: string) => Promise<void>;
}

const cell = { py: 0.75, verticalAlign: 'top' } as const;

const OUTCOME_COLOR = {
  drafted: 'success',
  excluded_by_rule: 'warning',
  failed: 'error',
  waiting: 'default',
  same_topic: 'default',
} as const;

// One morning's run: what it read, what it checked, every topic it considered
// and what happened to it, and what it passed over and why.
export default function RunReportView({
  run,
  onOpenItem,
  onAsk,
  onAddRule,
}: Props) {
  const { read, benchmarks: b } = run;
  const skipReasons = Object.entries(run.skipped.counts).sort(
    (x, y) => y[1] - x[1],
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Run of {formatTime(run.startedAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Daily limit {run.quota} drafts · Claude cost ${run.costUsd.toFixed(2)}
          {run.finishedAt &&
            ` · took ${Math.max(1, Math.round((+new Date(run.finishedAt) - +new Date(run.startedAt)) / 60000))} min`}
        </Typography>
      </Box>

      {run.failed.map(f => (
        <Alert severity="error" key={`${f.step}-${f.keyword ?? ''}`}>
          {f.step}
          {f.keyword ? ` (“${f.keyword}”)` : ''} failed: {f.error}
        </Alert>
      ))}

      <Section title="What it read">
        <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.5}>
          <Typography component="li" variant="body2">
            {fmt(read.keywords)} US searches with 500+ a month (Keyword Planner,{' '}
            {read.keywordSnapshot})
          </Typography>
          <Typography component="li" variant="body2">
            {fmt(read.gscQueries)} searches Dosaze appeared for in the last 28
            days (Search Console, through {formatDay(read.gscThrough)})
          </Typography>
          <Typography component="li" variant="body2">
            {fmt(read.articles)} existing blog articles and {fmt(read.products)}{' '}
            products in the five main collections (Shopify)
          </Typography>
          <Typography component="li" variant="body2">
            {run.rulesActive.length
              ? `${run.rulesActive.length} standing rule(s): ${run.rulesActive.map(r => `“${r.text}”`).join(' ')}`
              : 'No standing rules'}
          </Typography>
        </Stack>
      </Section>

      <Section
        title="Reviews done"
        subtitle="Published decisions whose 14/28/56-day check was due"
      >
        {run.reviews.length ? (
          <Table size="small">
            <TableBody>
              {run.reviews.map(r => (
                <TableRow key={`${r.itemId}-${r.day}`}>
                  <TableCell sx={cell}>
                    <Button
                      size="small"
                      sx={{ p: 0, textAlign: 'left' }}
                      onClick={() => onOpenItem(r.itemId)}
                    >
                      {r.title}
                    </Button>
                  </TableCell>
                  <TableCell sx={cell}>Day {r.day}</TableCell>
                  <TableCell sx={cell}>
                    <VerdictChip verdict={r.verdict} />
                  </TableCell>
                  <TableCell sx={{ ...cell, color: 'text.secondary' }}>
                    {r.note}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No reviews were due.
          </Typography>
        )}
      </Section>

      <Section
        title="What it learned"
        subtitle="Changes to how it scores topics"
      >
        {run.learning.length ? (
          run.learning.map(c => (
            <Typography variant="body2" key={c.format}>
              {c.because}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No changes (it needs 5 clear verdicts for a format, and adjusts at
            most weekly).
          </Typography>
        )}
      </Section>

      <Section
        title="Today’s top topics"
        subtitle="Best-scoring searches without a Dosaze page, and what happened to each"
      >
        {!!run.redrafted.length && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Also redrafted with reviewer feedback:{' '}
            {run.redrafted.map(r => `“${r.keyword}”`).join(', ')}.
          </Typography>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cell}>#</TableCell>
              <TableCell sx={cell}>Search</TableCell>
              <TableCell sx={cell}>Searches / mo</TableCell>
              <TableCell sx={cell}>Format</TableCell>
              <TableCell sx={cell}>Score</TableCell>
              <TableCell sx={cell}>What happened</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {run.candidates.map(c => (
              <TableRow key={c.topicKey}>
                <TableCell sx={cell}>{c.rank}</TableCell>
                <TableCell sx={cell}>
                  {c.itemId ? (
                    <Button
                      size="small"
                      sx={{ p: 0, textAlign: 'left' }}
                      onClick={() => onOpenItem(c.itemId!)}
                    >
                      {c.keyword}
                    </Button>
                  ) : (
                    c.keyword
                  )}
                  {c.source && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Adapting the US article “{c.source}”
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cell}>{fmt(c.monthlySearches)}</TableCell>
                <TableCell sx={cell}>{FORMAT_LABEL[c.format]}</TableCell>
                <TableCell sx={cell}>{fmt(c.score)}</TableCell>
                <TableCell sx={cell}>
                  <Chip
                    size="small"
                    color={OUTCOME_COLOR[c.outcome]}
                    label={OUTCOME_LABEL[c.outcome]}
                  />
                  {c.note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {c.note}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      {run.snippets && (
        <Section
          title="Title & description rewrites"
          subtitle={`${fmt(run.snippets.considered)} existing articles qualified today: Google shows them often but fewer people click than Dosaze’s usual rate for their position`}
        >
          {!!run.snippets.candidates.length && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cell}>#</TableCell>
                  <TableCell sx={cell}>Article</TableCell>
                  <TableCell sx={cell}>Shown (28 days)</TableCell>
                  <TableCell sx={cell}>Position</TableCell>
                  <TableCell sx={cell}>Click-through vs usual</TableCell>
                  <TableCell sx={cell}>What happened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.snippets.candidates.map(c => (
                  <TableRow key={c.articleId}>
                    <TableCell sx={cell}>{c.rank}</TableCell>
                    <TableCell sx={cell}>
                      {c.itemId ? (
                        <Button
                          size="small"
                          sx={{ p: 0, textAlign: 'left' }}
                          onClick={() => onOpenItem(c.itemId!)}
                        >
                          {c.title}
                        </Button>
                      ) : (
                        c.title
                      )}
                    </TableCell>
                    <TableCell sx={cell}>{fmt(c.impressions)}</TableCell>
                    <TableCell sx={cell}>{c.position}</TableCell>
                    <TableCell sx={cell}>
                      {(c.ctr * 100).toFixed(2)}% vs{' '}
                      {(c.benchmarkCtr * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell sx={cell}>
                      <Chip
                        size="small"
                        color={OUTCOME_COLOR[c.outcome]}
                        label={
                          c.outcome === 'drafted'
                            ? 'Rewritten'
                            : OUTCOME_LABEL[c.outcome]
                        }
                      />
                      {c.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {c.note}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Table size="small">
            <TableBody>
              {Object.entries(run.snippets.skipped.counts)
                .sort((x, y) => y[1] - x[1])
                .map(([reason, n]) => (
                  <TableRow key={reason}>
                    <TableCell sx={cell}>
                      Passed over: {SNIPPET_SKIP_LABEL[reason] ?? reason}
                    </TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 600 }}>
                      {fmt(n)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {run.refreshes && (
        <Section
          title="Article refreshes"
          subtitle={`${fmt(run.refreshes.considered)} existing articles qualified: Google shows them a lot just below the top spots, where climbing a few places would add clicks. ${fmt(run.refreshes.waitingForApproval)} were already waiting for approval.`}
        >
          {!!run.refreshes.candidates.length && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cell}>#</TableCell>
                  <TableCell sx={cell}>Article</TableCell>
                  <TableCell sx={cell}>Shown / clicks (28 days)</TableCell>
                  <TableCell sx={cell}>Position</TableCell>
                  <TableCell sx={cell}>Clicks to gain</TableCell>
                  <TableCell sx={cell}>What happened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.refreshes.candidates.map(c => (
                  <TableRow key={c.articleId}>
                    <TableCell sx={cell}>{c.rank}</TableCell>
                    <TableCell sx={cell}>
                      {c.itemId ? (
                        <Button
                          size="small"
                          sx={{ p: 0, textAlign: 'left' }}
                          onClick={() => onOpenItem(c.itemId!)}
                        >
                          {c.title}
                        </Button>
                      ) : (
                        c.title
                      )}
                    </TableCell>
                    <TableCell sx={cell}>
                      {fmt(c.impressions)} / {fmt(c.clicks)}
                    </TableCell>
                    <TableCell sx={cell}>{c.position}</TableCell>
                    <TableCell sx={cell}>~{fmt(c.score)} a month</TableCell>
                    <TableCell sx={cell}>
                      <Chip
                        size="small"
                        color={OUTCOME_COLOR[c.outcome]}
                        label={
                          c.outcome === 'drafted'
                            ? 'Refreshed'
                            : OUTCOME_LABEL[c.outcome]
                        }
                      />
                      {c.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {c.note}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Table size="small">
            <TableBody>
              {Object.entries(run.refreshes.skipped.counts)
                .sort((x, y) => y[1] - x[1])
                .map(([reason, n]) => (
                  <TableRow key={reason}>
                    <TableCell sx={cell}>
                      Passed over: {REFRESH_SKIP_LABEL[reason] ?? reason}
                    </TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 600 }}>
                      {fmt(n)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {run.merges && (
        <Section
          title="Duplicate merges"
          subtitle={`${fmt(run.merges.groups)} groups of our own articles compete for the same searches. ${fmt(run.merges.waitingForApproval)} merges were already waiting for approval.`}
        >
          {!!run.merges.candidates.length && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cell}>#</TableCell>
                  <TableCell sx={cell}>Page that stays</TableCell>
                  <TableCell sx={cell}>Pages in the group</TableCell>
                  <TableCell sx={cell}>Shown / clicks (28 days, all)</TableCell>
                  <TableCell sx={cell}>What happened</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.merges.candidates.map(c => (
                  <TableRow key={c.articleId}>
                    <TableCell sx={cell}>{c.rank}</TableCell>
                    <TableCell sx={cell}>
                      {c.itemId ? (
                        <Button
                          size="small"
                          sx={{ p: 0, textAlign: 'left' }}
                          onClick={() => onOpenItem(c.itemId!)}
                        >
                          {c.title}
                        </Button>
                      ) : (
                        c.title
                      )}
                    </TableCell>
                    <TableCell sx={cell}>{c.pages}</TableCell>
                    <TableCell sx={cell}>
                      {fmt(c.impressions)} / {fmt(c.clicks)}
                    </TableCell>
                    <TableCell sx={cell}>
                      <Chip
                        size="small"
                        color={OUTCOME_COLOR[c.outcome]}
                        label={
                          c.outcome === 'drafted'
                            ? 'Merge drafted'
                            : OUTCOME_LABEL[c.outcome]
                        }
                      />
                      {c.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {c.note}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Table size="small">
            <TableBody>
              {Object.entries(run.merges.skipped.counts)
                .sort((x, y) => y[1] - x[1])
                .map(([reason, n]) => (
                  <TableRow key={reason}>
                    <TableCell sx={cell}>
                      Waiting: {MERGE_SKIP_LABEL[reason] ?? reason}
                    </TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 600 }}>
                      {fmt(n)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Section>
      )}

      <Section
        title="What it passed over, and why"
        subtitle="Searches that didn’t become a candidate"
      >
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            {skipReasons.map(([reason, n]) => (
              <TableRow key={reason}>
                <TableCell sx={cell}>{SKIP_LABEL[reason] ?? reason}</TableCell>
                <TableCell sx={{ ...cell, fontWeight: 600 }}>
                  {fmt(n)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Biggest examples of each</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                {run.skipped.top.map(s => (
                  <TableRow key={`${s.reason}-${s.keyword}`}>
                    <TableCell sx={cell}>{s.keyword}</TableCell>
                    <TableCell sx={cell}>{fmt(s.monthlySearches)}/mo</TableCell>
                    <TableCell sx={{ ...cell, color: 'text.secondary' }}>
                      {s.detail}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Section>

      <Section
        title="Numbers behind the expected outcomes"
        subtitle="Dosaze’s own Search Console history"
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          New Dosaze articles usually rank around position{' '}
          {b.newArticlePosition['28'].p50} in their first 28 days (8 in 10
          between {b.newArticlePosition['28'].p10} and{' '}
          {b.newArticlePosition['28'].p90}) and {b.newArticlePosition['56'].p50}{' '}
          in days 29–56, across {fmt(b.newArticlePosition['56'].articles)}{' '}
          articles published since June 2025.
        </Typography>
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              Click-through rate by Google position (last 90 days, non-brand)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cell}>Position</TableCell>
                  <TableCell sx={cell}>Measured</TableCell>
                  <TableCell sx={cell}>Used (smoothed)</TableCell>
                  <TableCell sx={cell}>Impressions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {b.ctrCurve.map(p => (
                  <TableRow key={p.position}>
                    <TableCell sx={cell}>
                      {p.position === 21 ? '21+' : p.position}
                    </TableCell>
                    <TableCell sx={cell}>
                      {(p.rawCtr * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell sx={cell}>{(p.ctr * 100).toFixed(2)}%</TableCell>
                    <TableCell sx={cell}>{fmt(p.impressions)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Section>

      <Section title="Questions about this run">
        <AskBox
          thread={run.thread}
          placeholder="e.g. Why wasn’t “silk pillowcase” picked? Why were so many searches skipped?"
          onAsk={onAsk}
          onAddRule={onAddRule}
        />
      </Section>
    </Stack>
  );
}
